/**
 * Theme colors for the application
 * Provides consistent color palette for light and dark modes
 */
const theme = {
	// Text colors
	text: {
		primary: 'text-gray-900 dark:text-white',
		secondary: 'text-gray-600 dark:text-gray-300',
		brand: 'text-link dark:text-link/90',
		inactive: 'text-gray-400 dark:text-gray-500',
		error: 'text-red-600 dark:text-red-400',
		success: 'text-green-600 dark:text-green-400',
		warning: 'text-yellow-600 dark:text-yellow-400',
		info: 'text-blue-500 dark:text-blue-300',
		muted: 'text-gray-500 dark:text-gray-400',
		mutedHover: 'hover:text-gray-600 dark:hover:text-gray-400',
		link: 'text-link hover:text-link/80 dark:text-link dark:hover:text-link/90',
		brandHover: 'hover:text-link/80 dark:hover:text-link/90',
		current: 'text-blue-500 dark:text-blue-300',
		black: 'text-gray-800',
		white: 'text-white',
	},

	// Background colors
	bg: {
		primary: 'bg-white dark:bg-gray-900',
		secondary: 'bg-gray-50 dark:bg-gray-800',
		tertiary: 'bg-gray-200 dark:bg-gray-700',
		brand: 'bg-blue-400 dark:bg-blue-500',
		badge: 'bg-[#FF8A3A] dark:bg-[#FF8A3A]/80',
		hover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
		selected: 'bg-gray-100 dark:bg-gray-700',
		error: 'bg-red-500 dark:bg-red-900',
		errorHover: 'hover:bg-red-100 dark:hover:bg-red-800',
		success: 'bg-green-50 dark:bg-green-900',
		warning: 'bg-yellow-50 dark:bg-yellow-900',
		info: 'bg-blue-50 dark:bg-blue-900',
		accent: 'bg-blue-50 dark:bg-blue-900',
		gradientPrimary:
			'from-blue-500/[0.12] dark:from-blue-500/[0.24] to-blue-500/[0.04]',
		fill: 'fill-blue-700 dark:fill-blue-400 hover:fill-blue-600 dark:hover:fill-blue-400',
		upvote:
			'fill-blue-500 dark:fill-blue-400 hover:fill-blue-600 dark:hover:fill-blue-400',
		inactive: 'bg-gray-100 dark:bg-gray-800',
	},

	// Border colors
	border: {
		primary: 'border-gray-200 dark:border-gray-700',
		secondary: 'border-gray-300 dark:border-gray-600',
		brand: 'border-blue-600 dark:border-blue-500',
		focus: 'focus:border-blue-500 dark:focus:border-blue-400',
		error: 'border-red-500 dark:border-red-400',
		success: 'border-green-500 dark:border-green-400',
		warning: 'border-yellow-500 dark:border-yellow-400',
		ring: 'ring-blue-500 dark:ring-blue-400',
	},

	// Button colors
	button: {
		primary: {
			base: 'bg-blue-600 hover:bg-blue-700 text-white',
			dark: 'dark:bg-blue-500 dark:hover:bg-blue-600',
			disabled:
				'disabled:bg-blue-300 dark:disabled:bg-blue-800 disabled:cursor-not-allowed',
		},
		secondary: {
			base: 'bg-gray-100 hover:bg-gray-200 text-gray-800',
			dark: 'dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white',
			disabled:
				'disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed',
		},
		danger: {
			base: 'bg-red-600 hover:bg-red-700 text-white',
			dark: 'dark:bg-red-700 dark:hover:bg-red-800',
			disabled:
				'disabled:bg-red-300 dark:disabled:bg-red-800 disabled:cursor-not-allowed',
		},
		success: {
			base: 'bg-green-600 hover:bg-green-700 text-white',
			dark: 'dark:bg-green-700 dark:hover:bg-green-800',
			disabled:
				'disabled:bg-green-300 dark:disabled:bg-green-800 disabled:cursor-not-allowed',
		},
		warning: {
			base: 'bg-yellow-500 hover:bg-yellow-600 text-white',
			dark: 'dark:bg-yellow-600 dark:hover:bg-yellow-700',
			disabled:
				'disabled:bg-yellow-300 dark:disabled:bg-yellow-700 disabled:cursor-not-allowed',
		},
		tertiary: {
			base: 'bg-gray-100 hover:bg-gray-200 text-gray-800',
			dark: 'dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white',
			disabled:
				'disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed',
		},
		approve: {
			base: 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-300',
			dark: 'dark:bg-green-950/30 dark:hover:bg-green-900/40 dark:text-green-400 dark:border-green-700',
			disabled:
				'disabled:bg-green-50 dark:disabled:bg-green-950/20 disabled:text-green-400 dark:disabled:text-green-600 disabled:cursor-not-allowed',
			active: 'bg-green-500 hover:bg-green-500 text-white border-green-500 dark:bg-green-500 dark:hover:bg-green-500 dark:text-white dark:border-green-500',
		},
		reject: {
			base: 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-300',
			dark: 'dark:bg-red-950/30 dark:hover:bg-red-900/40 dark:text-red-400 dark:border-red-700',
			disabled:
				'disabled:bg-red-50 dark:disabled:bg-red-950/20 disabled:text-red-400 dark:disabled:text-red-600 disabled:cursor-not-allowed',
			active: 'bg-red-500 hover:bg-red-500 text-white border-red-500 dark:bg-red-500 dark:hover:bg-red-500 dark:text-white dark:border-red-500',
		},
		orange: {
			base: 'bg-[#FF8A3A] hover:bg-[#e87a30] text-white',
			dark: 'dark:bg-[#FF8A3A] dark:hover:bg-[#e87a30]',
			disabled:
				'disabled:bg-[#FF8A3A]/50 dark:disabled:bg-[#FF8A3A]/40 disabled:cursor-not-allowed',
		},
	},

	// Shadow styles
	shadow: {
		sm: 'shadow-sm',
		md: 'shadow',
		lg: 'shadow-lg',
		xl: 'shadow-xl',
		inner: 'shadow-inner',
		none: 'shadow-none',
		outline: 'shadow-outline',
	},

	// Transition styles
	transition: {
		default: 'transition-all duration-200 ease-in-out',
		slow: 'transition-all duration-300 ease-in-out',
		fast: 'transition-all duration-150 ease-in-out',
	},

	form: {
		checkbox: 'accent-blue-500',
	},
};

// Helper function to set theme mode (light/dark)
export const setThemeMode = (isDark: boolean): void => {
	if (isDark) {
		document.documentElement.classList.add('dark');
		localStorage.setItem('theme', 'dark');
	} else {
		document.documentElement.classList.remove('dark');
		localStorage.setItem('theme', 'light');
	}
};

// Helper function to get current theme mode
export const getThemeMode = (): 'dark' | 'light' => {
	if (typeof window === 'undefined') return 'light';

	const savedTheme = localStorage.getItem('theme');
	if (savedTheme === 'dark') return 'dark';
	if (savedTheme === 'light') return 'light';

	// Check system preference if no saved preference
	return window.matchMedia('(prefers-color-scheme: dark)').matches
		? 'dark'
		: 'light';
};

export default theme;
